'use client';

import React, { useCallback, useState } from 'react';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  AdvancedMarkerProps,
  InfoWindow,
  Map,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { createBreakpoint } from 'react-use';
import Image from 'next/image';
export type AnchorPointName = keyof typeof AdvancedMarkerAnchorPoint;
// import MarkerComponent from './MarkerComponent';
import InfoBoxComponent from './InfoBoxComponent';
import MapPin1 from '@images/map-pin1.svg';
import MapPin2 from '@images/map-pin2.svg';

interface Center {
  latitude: number | null;
  longitude: number | null;
  centerName: string;
  isNationalCenter: boolean;
  sfid: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  phone1?: string;
  phone2?: string;
  email?: string;
  centerMode?: string | null;
}

interface GoogleMapComponentProps {
  allCenters: Center[];
  // other props
}

const useBreakpoint = createBreakpoint({ XL: 1280, L: 768, S: 350 });

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({
  allCenters,
}) => {
  //const iconBase = '/images/';

  const features = allCenters
    ? allCenters
        .filter(
          (center) =>
            center.latitude !== null &&
            center.latitude !== undefined &&
            center.longitude !== null &&
            center.longitude !== undefined
        )
        .map((center, index) => {
          return {
            position: {
              lat: center.latitude,
              lng: center.longitude,
            },
            title: center.centerName,
            zIndex: index,
            icon: center.isNationalCenter ? MapPin2 : MapPin1,
            data: center,
            id: center.sfid,
          };
        })
    : [];

  const breakpoint = useBreakpoint();

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [anchorPoint, setAnchorPoint] = useState('BOTTOM' as AnchorPointName);
  const [selectedMarker, setSelectedMarker] =
    useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const onMarkerClick = useCallback(
    (
      id: string | null,
      data,
      marker?: google.maps.marker.AdvancedMarkerElement
    ) => {
      setSelectedId(id);
      setSelectedCenter(data);

      if (marker) {
        setSelectedMarker(marker);
      }

      if (id !== selectedId) {
        setInfoWindowShown(true);
      } else {
        setInfoWindowShown((isShown) => !isShown);
      }
    },
    [selectedId]
  );

  const onMapClick = useCallback(() => {
    setSelectedId(null);
    setSelectedMarker(null);
    setInfoWindowShown(false);
  }, []);

  const handleInfowindowCloseClick = useCallback(
    () => setInfoWindowShown(false),
    []
  );

  let position = { lat: 43.4142989, lng: -124.2301242 };
  let zoom = 4;

  if (breakpoint === 'S') {
    position = { lat: 40.49983925459706, lng: -101.63480276952534 };
    zoom = 3;
  }

  return (
    <Map
      id="map"
      style={{ height: 'calc(100vh - 75px)' }}
      mapId={process.env.NEXT_PUBLIC_GOOGLE_MAP_ID || 'DEMO_MAP_ID'}
      defaultZoom={zoom}
      defaultCenter={position}
      gestureHandling={'cooperative'}
      onClick={onMapClick}
      clickableIcons={false}
      disableDefaultUI
    >
      {features.map(({ id, zIndex, position, data, icon }) => {
        return (
          <React.Fragment key={id}>
            <AdvancedMarkerWithRef
              position={position}
              zIndex={zIndex}
              anchorPoint={AdvancedMarkerAnchorPoint[anchorPoint]}
              className="custom-marker"
              onMarkerClick={(
                marker: google.maps.marker.AdvancedMarkerElement
              ) => onMarkerClick(id, data, marker)}
            >
              <div
                className={`custom-html-content ${selectedId === id ? 'selected' : ''}`}
              >
                <Image src={icon} width={62} height={62} alt="map-pin" />
              </div>
            </AdvancedMarkerWithRef>
          </React.Fragment>
        );
      })}

      {infoWindowShown && selectedMarker && (
        <InfoWindow
          anchor={selectedMarker}
          pixelOffset={[0, -2]}
          onCloseClick={handleInfowindowCloseClick}
        >
          <InfoBoxComponent center={selectedCenter} />
        </InfoWindow>
      )}
    </Map>
    // <GoogleMap
    //   mapContainerStyle={{ width: '100%', height: '400px' }}
    //   zoom={zoom}
    //   center={position}
    //   options={{ mapTypeControl: false }}
    // >
    //   {features.map((feature) => (
    //     <MarkerComponent
    //       key={feature.sfid}
    //       {...feature}
    //       selectedMarker={selectedMarker}
    //       setSelectedMarker={setSelectedMarker}
    //     />
    //   ))}
    // </GoogleMap>
  );
};

export const AdvancedMarkerWithRef = (
  props: AdvancedMarkerProps & {
    onMarkerClick: (marker: google.maps.marker.AdvancedMarkerElement) => void;
  }
) => {
  const { children, onMarkerClick, ...advancedMarkerProps } = props;
  const [markerRef, marker] = useAdvancedMarkerRef();

  return (
    <AdvancedMarker
      onClick={() => {
        if (marker) {
          onMarkerClick(marker);
        }
      }}
      // @ts-ignore: Suppress type error for ref
      ref={markerRef}
      {...advancedMarkerProps}
    >
      {children}
    </AdvancedMarker>
  );
};

export default GoogleMapComponent;
